// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  Alert,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  Modal,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type { DeviceRequest, Extension, MedicationRequest, Patient, ServiceRequest } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconSearch } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { type JSX, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useRole } from '../../hooks/useRole';
import { hasPermission } from '../../utils/permissions';
import { isReferralMedicationRequest } from '../../utils/referral-pdf';
import { NewOrderModal } from '../patient/OrdersTab';

type WorklistCategory = 'dme' | 'rpm' | 'rx' | 'test' | 'referral';
type TypeFilter = 'all' | WorklistCategory;

interface WorklistRow {
  id: string;
  resourceType: 'ServiceRequest' | 'MedicationRequest' | 'DeviceRequest';
  resourceId: string;
  category: WorklistCategory;
  patientId?: string;
  patientName: string;
  mrn: string;
  title: string;
  party: string;
  status?: string;
  dmeStatus?: string;
  provider: string;
  date?: string;
  searchable: string;
}

const GROUPS: { category: WorklistCategory; label: string }[] = [
  { category: 'dme', label: 'DME & supplies' },
  { category: 'rpm', label: 'RPM' },
  { category: 'rx', label: 'Prescriptions' },
  { category: 'test', label: 'Tests & studies' },
  { category: 'referral', label: 'Referrals' },
];
const THERAPY_SYSTEM = 'http://polarissleep.com/therapies';
const RPM_SYSTEM = 'https://polarissleep.com/fhir/prescription-type';
const HSAT_PATTERN = /hsat|home sleep apnea test|watchpat/i;
const DME_STATUS_EXTENSION = 'https://polarissleep.com/fhir/StructureDefinition/dme-order-status';
const DME_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'awaiting-supplier', label: 'Awaiting supplier' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
];

function patientReference(resource: ServiceRequest | MedicationRequest | DeviceRequest): string | undefined {
  return resource.subject?.reference?.split('/')[1];
}

function patientLabel(patient: Patient | undefined, id: string | undefined): { name: string; mrn: string } {
  const fallbackName = [patient?.name?.[0]?.given?.join(' '), patient?.name?.[0]?.family].filter(Boolean).join(' ');
  return {
    name: patient?.name?.[0]?.text || fallbackName || id || 'Unknown patient',
    mrn: patient?.identifier?.find((identifier) => identifier.value)?.value ?? '—',
  };
}

function referralTarget(request: MedicationRequest): string | undefined {
  return request.note
    ?.flatMap((note) => note.text?.split('\n') ?? [])
    .find((line) => line.startsWith('Referral target:'))
    ?.slice('Referral target:'.length)
    .trim();
}

export function categoryFor(resource: ServiceRequest | MedicationRequest | DeviceRequest): WorklistCategory {
  if (resource.resourceType === 'DeviceRequest') {
    return 'dme';
  }
  if (resource.resourceType === 'ServiceRequest') {
    const text = `${resource.code?.text ?? ''} ${resource.code?.coding?.map((coding) => coding.display).join(' ') ?? ''}`;
    return /referral/i.test(text) ? 'referral' : 'test';
  }
  // Referral orders are MedicationRequests with the referral-type coding; same precedence as the patient Orders tab.
  if (isReferralMedicationRequest(resource)) {
    return 'referral';
  }
  const coding = resource.medicationCodeableConcept?.coding ?? [];
  if (coding.some((item) => item.system === RPM_SYSTEM)) {
    return 'rpm';
  }
  if (coding.some((item) => item.system === THERAPY_SYSTEM)) {
    return 'dme';
  }
  return 'rx';
}

export function rowFor(
  resource: ServiceRequest | MedicationRequest | DeviceRequest,
  patients: Map<string, Patient>
): WorklistRow | undefined {
  if (!resource.id) {
    return undefined;
  }
  const patientId = patientReference(resource);
  const patient = patientId ? patients.get(patientId) : undefined;
  const { name, mrn } = patientLabel(patient, patientId);
  const category = categoryFor(resource);
  let title = 'DME order';
  if (resource.resourceType === 'ServiceRequest') {
    title = resource.code?.text ?? resource.code?.coding?.[0]?.display ?? 'Test order';
  } else if (resource.resourceType === 'MedicationRequest') {
    title = resource.medicationCodeableConcept?.text ?? 'Medication order';
  } else {
    title = resource.codeCodeableConcept?.text ?? resource.codeCodeableConcept?.coding?.[0]?.display ?? 'DME order';
  }
  const date = resource.authoredOn;
  const status = resource.status;
  const hasHsatStatus = category === 'test' && HSAT_PATTERN.test(title) && Boolean(status);
  const provider =
    resource.requester?.display ??
    (resource.resourceType === 'ServiceRequest' ? resource.performer?.[0]?.display : undefined) ??
    '—';
  let party = '—';
  if (resource.resourceType === 'ServiceRequest') {
    party = resource.performer?.[0]?.display ?? 'Polaris Sleep';
  } else if (resource.resourceType === 'MedicationRequest') {
    party =
      category === 'referral'
        ? (referralTarget(resource) ?? 'Polaris Sleep')
        : (resource.dispenseRequest?.performer?.display ?? '—');
  } else {
    party = resource.performer?.display ?? '—';
  }
  let displayOrderStatus: string | undefined;
  if (hasHsatStatus) {
    displayOrderStatus = status === 'active' ? 'Awaiting result' : status;
  }
  const dmeStatus =
    category === 'dme'
      ? resource.extension?.find((extension) => extension.url === DME_STATUS_EXTENSION)?.valueCode
      : undefined;
  return {
    id: `${resource.resourceType}/${resource.id}`,
    resourceType: resource.resourceType,
    resourceId: resource.id,
    category,
    patientId,
    patientName: name,
    mrn,
    title,
    party,
    status: displayOrderStatus,
    dmeStatus,
    provider,
    date,
    searchable: [name, mrn, title, party, provider, status, category, resource.id]
      .filter(Boolean)
      .join(' ')
      .toLowerCase(),
  };
}

function displayStatus(row: WorklistRow): string {
  if (row.dmeStatus) {
    return DME_STATUSES.find((option) => option.value === row.dmeStatus)?.label ?? row.dmeStatus;
  }
  if (row.status) {
    return row.status;
  }
  return '—';
}

function isCancelled(row: WorklistRow): boolean {
  return row.dmeStatus === 'cancelled' || ['cancelled', 'revoked', 'failure'].includes(row.status?.toLowerCase() ?? '');
}

interface NewOrderPatientModalProps {
  opened: boolean;
  onClose: () => void;
  onSelect: (patient: Patient) => void;
  patients: Patient[];
}

function NewOrderPatientModal({
  opened,
  onClose,
  onSelect,
  patients: availablePatients,
}: NewOrderPatientModalProps): JSX.Element {
  const medplum = useMedplum();
  const [patients, setPatients] = useState<Patient[]>(availablePatients);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const profile = medplum.getProfile();

  useEffect(() => {
    if (!opened) {
      return;
    }
    if (availablePatients.length) {
      setPatients(availablePatients);
      setLoading(false);
      return;
    }
    setLoading(true);
    medplum
      .searchResources('Patient', { _count: '1000' })
      .then((resources) =>
        setPatients(resources.filter((patient): patient is Patient & { id: string } => Boolean(patient.id)))
      )
      .catch(() => setPatients([]))
      .finally(() => setLoading(false));
  }, [availablePatients, medplum, opened]);

  const visiblePatients = patients.filter((patient) => {
    const text = [
      patient.name?.[0]?.text,
      ...(patient.name?.[0]?.given ?? []),
      patient.name?.[0]?.family,
      ...(patient.identifier ?? []).map((identifier) => identifier.value),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return !query.trim() || text.includes(query.trim().toLowerCase());
  });
  let patientList: JSX.Element;
  if (loading) {
    patientList = <Skeleton height={42} />;
  } else if (visiblePatients.length) {
    patientList = (
      <>
        {visiblePatients.map((patient) => {
          const fallbackName = [patient.name?.[0]?.given?.join(' '), patient.name?.[0]?.family]
            .filter(Boolean)
            .join(' ');
          const name = patient.name?.[0]?.text || fallbackName || 'Unknown patient';
          const mrn = patient.identifier?.find((identifier) => identifier.value)?.value ?? 'No MRN';
          return (
            <UnstyledButton
              key={patient.id}
              onClick={() => onSelect(patient)}
              px="sm"
              py={9}
              style={{ border: '1px solid #ced4da', borderRadius: 8, textAlign: 'left', background: '#fff' }}
            >
              <Group justify="space-between" wrap="nowrap">
                <Text fw={600}>{name}</Text>
                <Text size="xs" c="dimmed">
                  {mrn} · DOB {patient.birthDate ?? '—'}
                </Text>
              </Group>
            </UnstyledButton>
          );
        })}
      </>
    );
  } else {
    patientList = (
      <Text c="dimmed" ta="center" py="md">
        No patients found.
      </Text>
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={700} size="xl">
          New order
        </Text>
      }
      centered
      size={680}
      styles={{ header: { padding: '18px 26px 4px' }, body: { padding: '0 26px 16px' } }}
    >
      <Text size="sm" c="dimmed" mb="md">
        Choose the patient this order is for. The order files to their chart exactly as it would from the chart&apos;s
        Orders tab.
      </Text>
      <Divider mb="md" />
      <TextInput
        placeholder="Search patients by name or MRN"
        value={query}
        onChange={(event) => setQuery(event.currentTarget.value)}
        leftSection={<IconSearch size={14} />}
        mb="sm"
      />
      <Stack gap="xs" mah={265} style={{ overflowY: 'auto' }}>
        {patientList}
      </Stack>
      <Group mt="md" pt="sm" style={{ borderTop: '1px solid #dee2e6' }}>
        <Text size="sm" c="dimmed">
          Ordering provider:
        </Text>
        <Text size="sm" fw={600}>
          {profile?.name?.[0]?.text ?? 'Current provider'}
        </Text>
      </Group>
    </Modal>
  );
}

export function OrdersWorklist(): JSX.Element {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const { role, isAdmin } = useRole();
  const canHsat = hasPermission({ role, permission: 'place-hsat-order', isAdmin });
  const canRpm = hasPermission({ role, permission: 'prescribe-cpap', isAdmin });
  const canReferral = hasPermission({ role, permission: 'create-referral-order', isAdmin });
  const [rows, setRows] = useState<WorklistRow[]>([]);
  const [patientCatalog, setPatientCatalog] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [type, setType] = useState<TypeFilter>('all');
  const [status, setStatus] = useState('all');
  const [provider, setProvider] = useState('all');
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(1);
  const [loadError, setLoadError] = useState<string>();
  const [reloadKey, setReloadKey] = useState(0);
  const [patientPickerOpened, setPatientPickerOpened] = useState(false);
  const [newOrderOpened, setNewOrderOpened] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient>();
  const pageSize = 50;

  useEffect(() => {
    let disposed = false;
    setLoading(true);
    setLoadError(undefined);
    const requestCount = String(1000 + reloadKey);
    Promise.all([
      medplum.searchResources('Patient', { _count: requestCount }),
      medplum.searchResources('ServiceRequest', { _count: requestCount }),
      medplum.searchResources('MedicationRequest', { _count: requestCount }),
      medplum.searchResources('DeviceRequest', { _count: requestCount }),
    ])
      .then(([patients, services, medications, devices]) => {
        if (disposed) {
          return;
        }
        const patientMap = new Map(
          (patients as Patient[]).filter((patient) => patient.id).map((patient) => [patient.id as string, patient])
        );
        setPatientCatalog([...patientMap.values()]);
        setRows(
          [...(services as ServiceRequest[]), ...(medications as MedicationRequest[]), ...(devices as DeviceRequest[])]
            .map((resource) => rowFor(resource, patientMap))
            .filter((row): row is WorklistRow => Boolean(row))
        );
      })
      .catch(() => {
        if (!disposed) {
          setRows([]);
          setLoadError('Orders could not be loaded.');
        }
      })
      .finally(() => {
        if (!disposed) {
          setLoading(false);
        }
      });
    return () => {
      disposed = true;
    };
  }, [medplum, reloadKey]);

  const providers = useMemo(
    () => [...new Set(rows.map((row) => row.provider).filter((value) => value !== '—'))].sort(),
    [rows]
  );
  const filtered = useMemo(() => {
    const value = query.trim().toLowerCase();
    return rows
      .filter(
        (row) =>
          (type === 'all' || row.category === type) &&
          (status === 'all' || row.status === status) &&
          (provider === 'all' || row.provider === provider) &&
          (!value || row.searchable.includes(value))
      )
      .sort((a, b) => {
        if (sort === 'name') {
          return a.patientName.localeCompare(b.patientName);
        }
        if (sort === 'oldest') {
          return (a.date ?? '').localeCompare(b.date ?? '');
        }
        if (sort === 'status') {
          return Number(Boolean(b.status)) - Number(Boolean(a.status));
        }
        return (b.date ?? '').localeCompare(a.date ?? '');
      });
  }, [provider, query, rows, sort, status, type]);
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);
  const countPool = rows.filter((row) => {
    const value = query.trim().toLowerCase();
    return (
      (status === 'all' || row.status === status) &&
      (provider === 'all' || row.provider === provider) &&
      (!value || row.searchable.includes(value))
    );
  });

  const updateDmeStatus = async (row: WorklistRow, value: string | null): Promise<void> => {
    if (!value || row.category !== 'dme') {
      return;
    }
    const resource = await medplum.readResource(row.resourceType, row.resourceId);
    const extension = [
      ...(resource.extension ?? []).filter((item: Extension) => item.url !== DME_STATUS_EXTENSION),
      { url: DME_STATUS_EXTENSION, valueCode: value },
    ];
    await medplum.updateResource({ ...resource, extension });
    setRows((current) => current.map((item) => (item.id === row.id ? { ...item, dmeStatus: value } : item)));
    showNotification({
      color: 'green',
      message: `DME status updated to ${displayStatus({ ...row, dmeStatus: value })}.`,
    });
  };

  const clearFilters = (): void => {
    setQuery('');
    setType('all');
    setStatus('all');
    setProvider('all');
    setSort('newest');
    setPage(1);
  };

  let content: JSX.Element;
  if (loading) {
    content = (
      <Stack gap="xs">
        {[1, 2, 3, 4].map((value) => (
          <Skeleton key={value} height={42} radius="sm" />
        ))}
      </Stack>
    );
  } else if (loadError) {
    content = (
      <Alert color="red" title="Unable to load orders">
        <Group justify="space-between">
          <Text size="sm">{loadError}</Text>
          <Button size="compact-sm" onClick={() => setReloadKey((value) => value + 1)}>
            Retry
          </Button>
        </Group>
      </Alert>
    );
  } else if (filtered.length === 0) {
    content = (
      <Card withBorder ta="center" p="xl">
        <Text fw={600}>{rows.length ? 'No orders match these filters.' : 'No orders yet.'}</Text>
        {rows.length > 0 && (
          <Button variant="subtle" mt="sm" onClick={clearFilters}>
            Clear filters
          </Button>
        )}
      </Card>
    );
  } else {
    content = (
      <Stack gap="xl">
        {GROUPS.map((group) => {
          const grouped = paged.filter((row) => row.category === group.category);
          if (!grouped.length) {
            return null;
          }
          return (
            <div key={group.category}>
              <Title order={3} mb="xs">
                {group.label}
              </Title>
              <Table striped highlightOnHover withTableBorder style={{ tableLayout: 'fixed', width: '100%' }}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Patient</Table.Th>
                    <Table.Th>Order</Table.Th>
                    <Table.Th>Party / Provider</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Ordered</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {grouped.map((row) => (
                    <Table.Tr key={row.id}>
                      <Table.Td>
                        <Link to={row.patientId ? `/Patient/${row.patientId}/orders` : '/orders'}>
                          {row.patientName}
                        </Link>
                        <Text size="xs" c="dimmed">
                          {row.mrn}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text fw={600}>{row.title}</Text>
                        <Text size="xs" c="dimmed">
                          {row.resourceType}/{row.resourceId}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {row.party}
                        <Text size="xs" c="dimmed">
                          {row.provider}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {row.category === 'dme' ? (
                          <Select
                            size="xs"
                            value={row.dmeStatus ?? 'active'}
                            data={DME_STATUSES}
                            onChange={(value) => updateDmeStatus(row, value)}
                          />
                        ) : (
                          displayStatus(row)
                        )}
                      </Table.Td>
                      <Table.Td>{row.date ? dayjs(row.date).format('MMM D, YYYY') : '—'}</Table.Td>
                      <Table.Td>
                        {row.patientId && !isCancelled(row) && (
                          <Group gap={2} wrap="nowrap">
                            <Button
                              component={Link}
                              to={`/Patient/${row.patientId}/orders?orderId=${row.resourceId}&action=edit`}
                              variant="subtle"
                              size="compact-xs"
                            >
                              Edit
                            </Button>
                            <Button
                              component={Link}
                              to={`/Patient/${row.patientId}/orders?orderId=${row.resourceId}&action=cancel`}
                              variant="subtle"
                              color="red"
                              size="compact-xs"
                            >
                              Cancel
                            </Button>
                          </Group>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
          );
        })}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start" wrap="wrap">
        <div>
          <Title order={2}>Orders</Title>
          <Text c="dimmed" size="sm">
            Every order across the practice, grouped by order class.
          </Text>
        </div>
        <Button onClick={() => setPatientPickerOpened(true)}>+ New order</Button>
      </Group>
      <Card withBorder>
        <Stack gap="md">
          <Group gap="xs" wrap="wrap">
            {(
              [['all', 'All orders'], ...GROUPS.map((group) => [group.category, group.label])] as [TypeFilter, string][]
            ).map(([value, label]) => (
              <Button
                key={value}
                role="tab"
                aria-selected={type === value}
                size="compact-sm"
                variant={type === value ? 'filled' : 'subtle'}
                onClick={() => setType(value)}
              >
                {label}{' '}
                <Badge size="sm" ml={4}>
                  {value === 'all' ? countPool.length : countPool.filter((row) => row.category === value).length}
                </Badge>
              </Button>
            ))}
          </Group>
          <Group align="flex-end" wrap="wrap">
            <TextInput
              flex={1}
              miw={260}
              placeholder="Search order, patient, MRN, provider, status"
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              leftSection={<IconSearch size={14} />}
            />
            <Select
              label="HSAT status"
              w={150}
              value={status}
              onChange={(value) => setStatus(value ?? 'all')}
              data={[
                { value: 'all', label: 'All' },
                { value: 'Awaiting result', label: 'Awaiting result' },
                { value: 'resulted', label: 'Resulted' },
              ]}
            />
            <Select
              label="Ordered by"
              w={190}
              value={provider}
              onChange={(value) => setProvider(value ?? 'all')}
              data={[{ value: 'all', label: 'Anyone' }, ...providers.map((value) => ({ value, label: value }))]}
            />
            <Select
              label="Sort"
              w={150}
              value={sort}
              onChange={(value) => setSort(value ?? 'newest')}
              data={[
                { value: 'newest', label: 'Newest' },
                { value: 'oldest', label: 'Oldest' },
                { value: 'status', label: 'Needs attention' },
                { value: 'name', label: 'Patient A–Z' },
              ]}
            />
          </Group>
          {content}
          {!loading && filtered.length > pageSize && (
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filtered.length)} of {filtered.length}
              </Text>
              <Group gap="xs">
                <Button
                  size="compact-sm"
                  variant="default"
                  disabled={page === 1}
                  onClick={() => setPage((value) => value - 1)}
                >
                  Previous
                </Button>
                <Button
                  size="compact-sm"
                  variant="default"
                  disabled={page * pageSize >= filtered.length}
                  onClick={() => setPage((value) => value + 1)}
                >
                  Next
                </Button>
              </Group>
            </Group>
          )}
        </Stack>
      </Card>
      <NewOrderPatientModal
        opened={patientPickerOpened}
        onClose={() => setPatientPickerOpened(false)}
        patients={patientCatalog}
        onSelect={(patient) => {
          setSelectedPatient(patient);
          setPatientPickerOpened(false);
          setNewOrderOpened(true);
        }}
      />
      <NewOrderModal
        opened={newOrderOpened}
        canHsat={canHsat}
        canRpm={canRpm}
        canReferral={canReferral}
        onSelect={(orderType) => {
          setNewOrderOpened(false);
          if (selectedPatient?.id) {
            navigate(`/Patient/${selectedPatient.id}/orders?newOrder=${orderType}`);
          }
        }}
        onClose={() => setNewOrderOpened(false)}
      />
    </Stack>
  );
}
